import { Spinner } from './TranscribeButton';
import { LoadingStatus, ProgressInfo } from '../hooks/useLoadingState';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
    message?: string;
    fullScreen?: boolean;
    status?: LoadingStatus;
    operation?: string;
    progress?: ProgressInfo;
    estimatedTimeRemaining?: number;
}

function formatTime(ms: number): string {
    if (ms < 1000) return 'Less than a second';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds % 60} second${seconds % 60 !== 1 ? 's' : ''}`;
}

export function LoadingOverlay({ 
    message, 
    fullScreen = false, 
    status = LoadingStatus.Loading,
    operation = '',
    progress,
    estimatedTimeRemaining
}: Props) {
    const containerClasses = fullScreen
        ? 'fixed inset-0 z-50 flex items-center justify-center bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm transition-all duration-200 mobile-py-safe mobile-px-safe touch-none select-none'
        : 'absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-lg transition-all duration-200 touch-none select-none';

    return (
        <AnimatePresence>
            <motion.div 
                className={containerClasses}
                role="alert"
                aria-live="polite"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <div className="text-center px-6 py-4 rounded-xl bg-white/90 dark:bg-gray-800/90 shadow-lg backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-3">
                        <Spinner text={operation || message || 'Loading...'} />
                        
                        {message && (
                            <motion.p 
                                className="text-sm font-medium text-gray-600 dark:text-gray-300"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {message}
                            </motion.p>
                        )}

                        {progress && (
                            <div className="w-full max-w-xs">
                                <div className="relative pt-1">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <span className="text-xs font-semibold inline-block text-primary">
                                                {progress.percentage}%
                                            </span>
                                        </div>
                                        {estimatedTimeRemaining && (
                                            <div className="text-right">
                                                <span className="text-xs font-semibold inline-block text-gray-500 dark:text-gray-400">
                                                    ~{formatTime(estimatedTimeRemaining)} remaining
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="overflow-hidden h-2 mt-1 text-xs flex rounded bg-gray-200 dark:bg-gray-700">
                                        <motion.div 
                                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${progress.percentage}%` }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    </div>
                                    {progress.total > 0 && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {progress.current.toLocaleString()} / {progress.total.toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {status === LoadingStatus.Error && (
                            <motion.p 
                                className="text-sm font-medium text-error"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                            >
                                An error occurred. Please try again.
                            </motion.p>
                        )}
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
